import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Form } from "react-bootstrap";

import { ITickerGame } from "@src/types/api";

import {
    globalActions,
    selectLeagueAgeLevels,
    selectTickerDate,
    selectTickerGames,
} from "@features/global/globalSlice";

import { useAppDispatch, useAppSelector } from "@hooks/reduxHooks";
import useBreakpoint from "@hooks/useBreakpoint";

import { defaultDateFormat, defaultTimeFormat, isXOrBigger } from "@lib/utils";

const GameItem = ({
    game,
    pausedRef,
}: {
    game: ITickerGame;
    pausedRef: React.MutableRefObject<boolean>;
}): React.JSX.Element => {
    const [showSummary, setShowSummary] = useState(false);
    const [popoverPos, setPopoverPos] = useState<{
        top: number;
        left: number;
    } | null>(null);
    const gameRef = useRef<HTMLSpanElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);
    const hasSummary =
        game.visitor.summary !== null || game.home.summary !== null;

    // Dismiss popover when interacting outside the game / popover (mobile)
    useEffect(() => {
        if (!showSummary) return;

        const handleOutside = (e: Event) => {
            const target = e.target as Node;
            if (
                gameRef.current &&
                !gameRef.current.contains(target) &&
                (!popoverRef.current || !popoverRef.current.contains(target))
            ) {
                setShowSummary(false);
                setPopoverPos(null);
                pausedRef.current = false;
            }
        };

        document.addEventListener("touchstart", handleOutside);
        return () => {
            document.removeEventListener("touchstart", handleOutside);
        };
    }, [showSummary, pausedRef]);

    const show = () => {
        if (!hasSummary || !gameRef.current) return;
        const rect = gameRef.current.getBoundingClientRect();
        setPopoverPos({
            top: rect.bottom + 8,
            left: rect.left + rect.width / 2,
        });
        setShowSummary(true);
    };

    const hide = () => {
        setShowSummary(false);
        setPopoverPos(null);
    };

    return (
        <span
            className={`ticker-game${hasSummary ? " has-summary" : ""}`}
            ref={gameRef}
            onMouseEnter={show}
            onMouseLeave={hide}
            onTouchStart={e => {
                if (!hasSummary) return;
                e.preventDefault();
                if (showSummary) {
                    hide();
                    pausedRef.current = false;
                } else {
                    show();
                    pausedRef.current = true;
                }
            }}
        >
            {game.designation && (
                <span className="ticker-designation">{game.designation}</span>
            )}
            <span className="ticker-team">{game.visitor.name}</span>
            <span className="ticker-score">{game.visitor.score ?? ""}</span>
            <span className="ticker-at">@</span>
            <span className="ticker-team">{game.home.name}</span>
            <span className="ticker-score">{game.home.score ?? ""}</span>
            {game.status === "Scheduled" && game.time && (
                <span className="ticker-time">
                    {defaultTimeFormat.format(new Date(game.time))}
                </span>
            )}
            {game.status === "Final" && (
                <span className="ticker-final">Final</span>
            )}
            {showSummary &&
                popoverPos &&
                createPortal(
                    <div
                        className="ticker-summary-popover"
                        ref={popoverRef}
                        style={{
                            top: popoverPos.top,
                            left: popoverPos.left,
                        }}
                    >
                        <div className="ticker-summary-arrow" />
                        {game.visitor.summary && (
                            <div className="ticker-summary-entry">
                                <b>{game.visitor.name} Summary</b>
                                <p>{game.visitor.summary}</p>
                            </div>
                        )}
                        {game.home.summary && (
                            <div className="ticker-summary-entry">
                                <b>{game.home.name} Summary</b>
                                <p>{game.home.summary}</p>
                            </div>
                        )}
                    </div>,
                    document.body,
                )}
        </span>
    );
};

const SCROLL_SPEED = 50; // pixels per second
const TICKER_AGE_KEY = "tickerAgeLevel";

const getSavedAgeLevel = (): number | undefined => {
    const saved = localStorage.getItem(TICKER_AGE_KEY);
    return saved ? Number(saved) : undefined;
};

const ScoreTicker = (): React.JSX.Element => {
    const dispatch = useAppDispatch();
    const [selectedAge, setSelectedAge] = useState<number | undefined>(
        getSavedAgeLevel,
    );
    const [copies, setCopies] = useState(2);
    const trackRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const singleRef = useRef<HTMLDivElement>(null);
    const offsetRef = useRef(0);
    const pausedRef = useRef(false);
    const breakpoint = useBreakpoint();

    const ageLevels = useAppSelector(selectLeagueAgeLevels);
    const games = useAppSelector(selectTickerGames);
    const tickerDate = useAppSelector(selectTickerDate);

    const formattedDate = tickerDate
        ? new Date(tickerDate).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
          })
        : new Date().toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
          });

    useEffect(() => {
        if (selectedAge !== undefined) {
            dispatch(globalActions.getTickerGames(selectedAge));
        }
    }, [dispatch, selectedAge]);

    useEffect(() => {
        offsetRef.current = 0;
    }, [selectedAge]);

    // Calculate how many copies we need to fill the track with no gaps
    useEffect(() => {
        if (!singleRef.current || !trackRef.current || games.length === 0)
            return;

        const measure = () => {
            const singleWidth = singleRef.current!.scrollWidth;
            const trackWidth = trackRef.current!.offsetWidth;
            // Need enough copies so content >= trackWidth + one full set for scrolling
            const needed = Math.ceil(trackWidth / singleWidth) + 1;
            setCopies(Math.max(2, needed));
        };

        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [games.length]);

    useEffect(() => {
        if (games.length === 0) return;

        let rafId: number;
        let lastTime: number | null = null;

        const tick = (timestamp: number) => {
            if (lastTime === null) lastTime = timestamp;
            const delta = timestamp - lastTime;
            lastTime = timestamp;

            if (!pausedRef.current && contentRef.current && singleRef.current) {
                const singleWidth = singleRef.current.scrollWidth;

                if (singleWidth > 0) {
                    offsetRef.current += (SCROLL_SPEED * delta) / 1000;
                    // Wrap by one set width — seamless because identical copies follow
                    offsetRef.current %= singleWidth;
                    contentRef.current.style.transform = `translateX(-${offsetRef.current}px)`;
                }
            }

            rafId = requestAnimationFrame(tick);
        };

        rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafId);
    }, [games.length, copies]);

    return (
        <div id="scoreTickerWrapper">
            <div className="ticker-age-select">
                <Form.Select
                    size="sm"
                    value={selectedAge ?? ""}
                    onChange={e => {
                        const val = e.target.value;
                        if (val) {
                            localStorage.setItem(TICKER_AGE_KEY, val);
                            setSelectedAge(Number(val));
                        } else {
                            localStorage.removeItem(TICKER_AGE_KEY);
                            setSelectedAge(undefined);
                        }
                    }}
                    aria-label="Select age level"
                >
                    <option value="">Age</option>
                    {ageLevels.map(age => (
                        <option key={age.id} value={age.id}>
                            {age.ageLevel}
                        </option>
                    ))}
                </Form.Select>
            </div>
            <div className="ticker-date">
                {isXOrBigger("md", breakpoint) && <>Recent Games </>}
                {formattedDate && selectedAge !== undefined ? (
                    <>
                        {isXOrBigger("md", breakpoint) && <>&bull; </>}
                        {isXOrBigger("md", breakpoint)
                            ? formattedDate
                            : defaultDateFormat.format(new Date(formattedDate))}
                    </>
                ) : null}
            </div>
            <div
                className={`ticker-track${selectedAge === undefined || games.length === 0 ? " no-mask" : ""}`}
                ref={trackRef}
                onMouseEnter={() => {
                    pausedRef.current = true;
                }}
                onMouseLeave={() => {
                    pausedRef.current = false;
                }}
            >
                {selectedAge === undefined ? (
                    <span className="ticker-no-games">
                        Please select an age level to view recent games
                    </span>
                ) : games.length > 0 ? (
                    <div className="ticker-scroll" ref={contentRef}>
                        {/* First copy used for measuring single-set width */}
                        <span ref={singleRef} className="ticker-set">
                            {games.map(game => (
                                <GameItem
                                    key={game.id}
                                    game={game}
                                    pausedRef={pausedRef}
                                />
                            ))}
                        </span>
                        {/* Additional copies to fill the track with no gaps */}
                        {Array.from({ length: copies - 1 }, (_, i) => (
                            <span key={`set-${i}`} className="ticker-set">
                                {games.map(game => (
                                    <GameItem
                                        key={`c${i}-${game.id}`}
                                        game={game}
                                        pausedRef={pausedRef}
                                    />
                                ))}
                            </span>
                        ))}
                    </div>
                ) : (
                    <span className="ticker-no-games">No games scheduled</span>
                )}
            </div>
        </div>
    );
};

export default ScoreTicker;
